import React, {useMemo} from "react";
import {
  Page,
  Text,
  View,
  Document as PdfDocument,
  StyleSheet,
  Font,
  Svg,
  Polygon,
  Image,
} from "@react-pdf/renderer";

import {
  calculatePdfFontSize,
  calculatePdfHeight,
  calculatePdfWidth,
  getPageOrientation,
  labelsNumberOnPage,
} from "../../../Utils";
import {FormPaperColorEnum, FormPrintColorEnum, FormPrintOptionsType} from "../../../Types";
import {FormLabelType} from "../Types";
import * as Icons from "../../../Icons/Components";
import {
  getAutoTextSize,
  getBannerLetterSpacing,
  getBannerSize,
  getBannerText,
} from "../Config";
import {getPdfLabelFromFormLabel} from "../Utils";
import {isPdfLabelCalculationType, isPdfLabelIconType, isPdfLabelSimpleType} from "../Types/Pdf";
import PdfSimple from "./PdfSimple";
import PdfCalculation from "./PdfCalculation";

{/* @ts-ignore */}
import BebasNeueRegular from "../../../Fonts/BebasNeue-Regular.ttf";
{/* @ts-ignore */}
import FuturaPTBook from "../Fonts/FuturaPT-Book.ttf";
{/* @ts-ignore */}
import SunbornSansOne from "../Fonts/Sunborn-SansOne.ttf";
{/* @ts-ignore */}
import SunbornSansOneSlant from "../Fonts/Sunborn-SansOneSlant.ttf";
{/* @ts-ignore */}
import LeagueSpartanBold from "../Fonts/LeagueSpartan-Bold.ttf";
import PdfIcon from "./PdfIcon";

Font.register({ family: 'BebasNeueRegular', src: BebasNeueRegular });
Font.register({ family: 'FuturaPT-Book', src: FuturaPTBook });
Font.register({ family: 'Sunborn', fonts: [
    { src: SunbornSansOne },
    { src: SunbornSansOneSlant, fontStyle: 'italic' },
]});
Font.register({ family: 'LeagueSpartan', fonts: [
    { src: LeagueSpartanBold, fontWeight: 'bold' },
]});

// No word wrap, see: https://v2.react-pdf.org/advanced#hyphenation
const hyphenationCallback = (word: any) => {
  return [word];
}
Font.registerHyphenationCallback(hyphenationCallback);

export const Pdf = ({printOptions, labels}: { printOptions: FormPrintOptionsType, labels: FormLabelType[] }) => {
  const HEADER_SEPARATOR_HEIGHT = printOptions.orientation === 'portrait' ? 45 : 29;
  const HEADER_SEPARATOR_WIDTH = printOptions.orientation === 'portrait' ? 150 : 110;
  const logo = printOptions.paperColor === FormPaperColorEnum.YELLOW ? "logo-min-supra-couleur.png" : "logo-min-supra-nb.png"
  const pageBackgroundColor = printOptions.paperColor === FormPaperColorEnum.WHITE ? "white" : "#F8D96E"
  const colorRed = printOptions.printColor === FormPrintColorEnum.BW ? "black" : "red"

  const styles = StyleSheet.create({
    page: {
      flexDirection: "row",
      flexWrap: "wrap",
      backgroundColor: pageBackgroundColor,
      margin: 0,
      padding: 0,
    },
    label: {
      backgroundColor: pageBackgroundColor,
    },
    header: {
      width: "100%",
      backgroundColor: "black",
      fontFamily: "Sunborn",
      fontWeight: "bold",
      textAlign: "center",
      zIndex: 1,
      position: "relative",
      display: "flex",
      alignItems: "center",
    },
    headerTitle: {
      color: pageBackgroundColor,
      maxLines: 1,
      textTransform: "uppercase",
    },
    headerSubtitle: {
      color:pageBackgroundColor,
      maxLines: 1,
      textTransform: "uppercase",
      position: "absolute",
      bottom: 0,
    },
    headerSeparator: {
      textAlign: "center",
      margin: "-1 auto 0 auto",
      backgroundColor: pageBackgroundColor,
      zIndex: 2,
    },
    information: {
      width: "100%",
      marginTop: calculatePdfHeight(10, printOptions.size),
      height: calculatePdfHeight(101, printOptions.size),
      display: "flex",
      flexDirection: "row",
    },
    informationContainer: {
      display: "flex",
      flexDirection: "row",
      alignItems: 'center',
    },
    informationIconRaw: {
      backgroundColor: "black",
      borderRadius: calculatePdfWidth(20, printOptions.size),
      width: calculatePdfWidth(100, printOptions.size),
      height: calculatePdfHeight(100, printOptions.size),
      marginLeft: calculatePdfWidth(20, printOptions.size),
      paddingTop: calculatePdfHeight(10, printOptions.size),
      paddingBottom: calculatePdfHeight(10, printOptions.size),
      paddingLeft: calculatePdfWidth(10, printOptions.size),
      paddingRight: calculatePdfWidth(10, printOptions.size),
      color: pageBackgroundColor,
      fontSize: calculatePdfFontSize(38, printOptions.size),
      lineHeight: calculatePdfFontSize(38, printOptions.size) + "px",
      fontFamily: "BebasNeueRegular",
      justifyContent: 'center',
      alignItems: 'center',
      textAlign: 'center',
      overflow: 'hidden',
      flexShrink: 0,
    },
    informationTextRaw: {
      marginLeft: calculatePdfWidth(20, printOptions.size),
    },
    informationTextTitle: {
      fontFamily: "LeagueSpartan",
      fontWeight: "bold",
      color: "black",
      maxLines: 1,
      textTransform: "uppercase",
    },
    informationTextSubTitle: {
      fontFamily: "Sunborn",
      color: "black",
      maxLines: 1,
      textTransform: "uppercase",
    },
    pricesRaw: {
      marginTop: calculatePdfHeight(14, printOptions.size),
    },
    footer: {
      position: "absolute",
      bottom: 0,
      width: "100%",
    },
    footerLegalNotice: {
      width: "100%",
      textAlign: "center",
      color: "black",
      fontFamily: "FuturaPT-Book",
      fontSize: calculatePdfFontSize(16, printOptions.size),
      paddingBottom: calculatePdfHeight(5, printOptions.size),
      maxLines: 1,
    },
    footerBaseLine: {
      backgroundColor: "black",
      textAlign: "center",
      color: pageBackgroundColor,
      fontFamily: "Sunborn",
      fontSize: calculatePdfFontSize(14, printOptions.size),
      width: "100%",
      paddingTop: calculatePdfHeight(7, printOptions.size),
      paddingBottom: calculatePdfHeight(7, printOptions.size),
    },
    footerLogo: {
      position: "absolute",
      top: calculatePdfHeight(5, printOptions.size),
      left: calculatePdfWidth(70, printOptions.size),
      height: calculatePdfHeight(25, printOptions.size),
    }
  });

  const themeLabelBySize = useMemo(() => ({
    A4: {
      portrait: {
        height: "29.7cm",
        width: "21cm",
      },
      landscape: {
        height: "21cm",
        width: "29.7cm",
      }
    },
    A5: {
      portrait: {
        height: "21cm",
        width: "14.85cm",
      },
      landscape: {
        height: "14.85cm",
        width: "21cm",
      }
    },
    A6: {
      portrait: {
        height: "14.85cm",
        width: "10.5cm",
      },
      landscape: {
        height: "10.5cm",
        width: "14.85cm",
      }
    },
    A7: {
      portrait: {
        height: "10.5cm",
        width: "7.425cm",
      },
      landscape: {
        height: "7.425cm",
        width: "10.5cm",
      }
    },
  }), []);

  const themeHeader = useMemo(() => ({
    portrait: {
      height: calculatePdfHeight(130, printOptions.size),
      justifyContent: "center",
    },
    landscape: {
      height: calculatePdfHeight(110, printOptions.size),
    }
  }), [printOptions.size]);

  const themeHeaderTitle = useMemo(() => ({
    portrait: {
    },
    landscape: {
      top: calculatePdfHeight(5, printOptions.size),
    }
  }), [printOptions.size]);

  const themeHeaderSubtitle = useMemo(() => ({
    portrait: {
      bottom: 0,
    },
    landscape: {
      bottom: calculatePdfHeight(7, printOptions.size),
    }
  }), [printOptions.size]);

  const themeHeaderSeparator = useMemo(() => ({
    portrait: {
      width: calculatePdfWidth(HEADER_SEPARATOR_WIDTH, printOptions.size),
      height: calculatePdfHeight(HEADER_SEPARATOR_HEIGHT, printOptions.size),
    },
    landscape: {
      width: calculatePdfWidth(HEADER_SEPARATOR_WIDTH, printOptions.size),
      height: calculatePdfHeight(HEADER_SEPARATOR_HEIGHT + 11, printOptions.size),
    }
  }), [HEADER_SEPARATOR_WIDTH, printOptions.size]);

  const themeInformation = useMemo(() => ({
    portrait: {
    },
    landscape: {
      justifyContent: 'center',
    }
  }), [printOptions.size]);

  const groupedLabels = [];
  for (let i = 0; i < labels.length; i += labelsNumberOnPage(printOptions.size)) {
    groupedLabels.push(labels.slice(i, i + labelsNumberOnPage(printOptions.size)));
  }

  // @ts-ignore
  return (
    <PdfDocument>
      {groupedLabels.map((labels, index) => (
        <Page
          orientation={getPageOrientation(printOptions.size, printOptions.orientation)}
          size={"A4"}
          style={{...styles.page}}
          key={'page' + index}
        >
          {labels.map((label) => {
            // @ts-ignore
            const Icon = Icons[label.icon.replace(/[^a-zA-Z]/g, '')] || null;

            const labelPdf = getPdfLabelFromFormLabel(label);

            return (
              // @ts-ignore
              <View key={'label' + label.id}>
                {/* @ts-ignore */}
                <View style={{...styles.label, ...themeLabelBySize[printOptions.size][printOptions.orientation]}}>
                  {/* @ts-ignore */}
                  <View style={{...styles.header, ...themeHeader[printOptions.orientation]}}>
                    {/* @ts-ignore */}
                    <Text style={{
                      ...styles.headerTitle,
                      ...themeHeaderTitle[printOptions.orientation],
                      fontSize: calculatePdfFontSize(getAutoTextSize(labelPdf.title, 60, 13), printOptions.size)
                    }}>
                      {!labelPdf.title ? ' ' : labelPdf.title}
                    </Text>
                    {/* @ts-ignore */}
                    <Text style={{
                      ...styles.headerSubtitle,
                      ...themeHeaderSubtitle[printOptions.orientation],
                      fontSize: calculatePdfFontSize(
                        getBannerSize(labelPdf.banner, printOptions.orientation),
                        printOptions.size
                      ),
                      letterSpacing: calculatePdfFontSize(getBannerLetterSpacing(label.banner, printOptions.orientation), printOptions.size)
                    }}>
                      {getBannerText(labelPdf.banner, printOptions.orientation)}
                    </Text>
                  </View>

                  {/* @ts-ignore */}
                  <View style={{...styles.headerSeparator, ...themeHeaderSeparator[printOptions.orientation]}}>
                    <Svg>
                      <Polygon
                        points={calculatePdfWidth(HEADER_SEPARATOR_WIDTH/2, printOptions.size) + "," + calculatePdfHeight(HEADER_SEPARATOR_HEIGHT, printOptions.size) +" 0,0 " + calculatePdfWidth(HEADER_SEPARATOR_WIDTH, printOptions.size) + ",0"}
                        fill="black"
                      />
                    </Svg>
                  </View>

                  {/* @ts-ignore */}
                  <View style={{...styles.information, ...themeInformation[printOptions.orientation]}}>
                    <View style={{...styles.informationContainer}}>
                      {Icon && (
                        <View style={styles.informationIconRaw}>
                          <Icon color={pageBackgroundColor} />
                        </View>
                      )}

                      <View style={styles.informationTextRaw}>
                        <Text style={{
                          ...styles.informationTextTitle,
                          fontSize: calculatePdfFontSize(getAutoTextSize(labelPdf.name, 62, Icon ? 9 : 10), printOptions.size),
                          lineHeight: calculatePdfFontSize(getAutoTextSize(labelPdf.name, 62, Icon ? 9 : 10),
                            printOptions.size) + 'px'
                        }}>
                          {labelPdf.name}
                        </Text>

                        <Text style={{
                          ...styles.informationTextSubTitle,
                          fontSize: calculatePdfFontSize(getAutoTextSize(labelPdf.format, 28, Icon ? 22 : 27), printOptions.size),
                        }}>
                          {labelPdf.format}
                        </Text>
                      </View>
                    </View>
                  </View>

                  <View style={styles.pricesRaw}>
                    {isPdfLabelSimpleType(labelPdf) && (
                      <PdfSimple printOptions={printOptions} label={labelPdf} colorRed={colorRed} />
                    )}

                    {isPdfLabelIconType(labelPdf) && (
                      <PdfIcon printOptions={printOptions} label={labelPdf} colorRed={colorRed} />
                    )}

                    {isPdfLabelCalculationType(labelPdf) && (
                      <PdfCalculation printOptions={printOptions} label={labelPdf} colorRed={colorRed} />
                    )}
                  </View>

                  <View style={styles.footer}>
                    <View style={styles.footerLegalNotice}>
                      <Text>{labelPdf.details}</Text>
                    </View>

                    <View style={styles.footerBaseLine}>
                      <Image src={logo} style={styles.footerLogo}/>

                      <Text>SUPRAPHARM - SANTEZ VOUS MIEUX</Text>
                    </View>
                  </View>
                </View>
              </View>
            )
          })}
        </Page>
      ))}
    </PdfDocument>
  );
};

export default Pdf;
